import { Tabs, Button, Group, Container, Text, Divider, Badge } from "@mantine/core";
import React, { useState } from "react";
import ReactJson from "react-json-view";
import _ from "lodash";
import { useTranslation } from "react-i18next";
import { shouldMarkWizardSubmitted } from "../helpers/formSubmitHelpers";

const FormWizard = ({
  wizard,
  getChildContainers,
  getChildFields,
  renderContainer,
  renderField,
  confirmSubmit,
  showDivider = true,
  form,
  ...props
}) => {
  const [currentTab, setCurrentTab] = useState(wizard.defaultValue);
  const [confirming, setConfirming] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const { t } = useTranslation();
  const handleConfirm = async () => {
    if (!_.isFunction(confirmSubmit)) return console.log("Confirm Submit");
    if (confirming || submitted) return;
    if (form.isUniqueCheckPending?.()) return;

    const validation = form.validate?.();
    if (validation?.hasErrors) return;

    setConfirming(true);
    try {
      const result = await confirmSubmit();
      if (!shouldMarkWizardSubmitted(result)) return;
      setSubmitted(true);
    } finally {
      setConfirming(false);
    }
  };

  const toPage = (index) => {
    if (index < 0 || index > wizard.steps.length - 1) return;
    if (index === wizard.steps.length - 1) {
      form.validate?.();
    }
    setCurrentTab(wizard.steps[index].label);
  };

  const uniqueCheckPending = form.isUniqueCheckPending?.() ?? false;

  const renderNextButton = (index) => {
    return index === wizard.steps.length - 1 ? (
      <Button
        onClick={handleConfirm}
        disabled={
          submitted || !_.isEmpty(form.errors) || uniqueCheckPending
        }
        loading={confirming || uniqueCheckPending}
      >
        {submitted
          ? t("Submitted")
          : uniqueCheckPending
            ? t("Checking...")
            : t("Confirm")}
      </Button>
    ) : (
      <Button onClick={() => toPage(index + 1)}>{t("Next")} </Button>
    );
  };
  const { t: translate } = useTranslation();

  const renderPrevButton = (index) => {
    return index === 0 ? null : (
      <Button variant="default" onClick={() => toPage(index - 1)}>
        {t("Prev")}
      </Button>
    );
  };

  return (
    <>
      {/* {<ReactJson src={wizard} style={{ background: "white" }} collapsed />} */}
      {showDivider && <Divider />}
      <Tabs value={currentTab} {...wizard.props} key={wizard.key}>
        <Tabs.List pt={"md"}>
          {wizard.steps?.map(({ label, key, warn }, index) => {
            const warned =
              typeof warn === "function" ? warn(form?.values) : false;
            return (
              <Tabs.Tab
                key={key}
                onClick={() => toPage(index)}
                value={label}
              >
                <Group spacing={4} noWrap>
                  <Text size="xs" color={warned ? "red" : undefined}>
                    {translate(label)}
                  </Text>
                  {warned ? (
                    <Badge size="xs" color="red" radius={0}>
                      {translate("Expired")}
                    </Badge>
                  ) : null}
                </Group>
              </Tabs.Tab>
            );
          })}
        </Tabs.List>
        {wizard.steps?.map(({ label, key }, index) => (
          <Tabs.Panel key={key} value={label} ml="xl" pt="xl" pb="xl">
            <Container
              sx={{
                display: "flex",
                flexDirection: "column",
                minHeight: wizard.props?.style?.minHeight ?? 400,
              }}
            >
              <div style={{ flex: 1, paddingBottom: 16 }}>
                {getChildContainers(key).map((c) => (
                  <React.Fragment key={c.key}>{renderContainer(c)}</React.Fragment>
                ))}
                {getChildFields(key).map((f) => (
                  <React.Fragment key={`${f.parent}-${f.name}`}>
                    {renderField(f)}
                  </React.Fragment>
                ))}
              </div>
              <Group position="right" mt="md">
                <span>{renderPrevButton(index)}</span>
                <span>{renderNextButton(index)}</span>
              </Group>
            </Container>
          </Tabs.Panel>
        ))}
      </Tabs>
    </>
  );
};

export default FormWizard;
